import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "../lib/api";
import { Clock, Crown, SoccerBall } from "@phosphor-icons/react";

const COMPUTE_TICK_MS = 1000;

function timeUntil(targetIso) {
  if (!targetIso) return null;
  const now = Date.now();
  const target = new Date(targetIso).getTime();
  const diff = target - now;
  if (diff <= 0) return { live: true };
  const days = Math.floor(diff / (24 * 60 * 60 * 1000));
  const hours = Math.floor((diff % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
  const minutes = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
  const seconds = Math.floor((diff % (60 * 1000)) / 1000);
  return { days, hours, minutes, seconds, live: false };
}

/**
 * Live countdown to the next un-played EPL fixture, fetched from /api/season/kickoff.
 * variant: "banner" (full-width strip) | "card" (boxed urgency on Pricing)
 */
export const KickoffCountdown = ({ variant = "banner", showCta = true }) => {
  const [meta, setMeta] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await apiClient.get("/season/kickoff");
        setMeta(data);
      } catch {
        setLoadFailed(true);
      }
    })();
  }, []);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), COMPUTE_TICK_MS);
    return () => clearInterval(t);
  }, []);

  if (loadFailed || !meta || !meta.kickoff) return null;
  const remaining = timeUntil(meta.kickoff);
  if (!remaining) return null;

  if (variant === "card") {
    return <Card remaining={remaining} meta={meta} now={now} showCta={showCta} />;
  }
  return <Banner remaining={remaining} meta={meta} showCta={showCta} />;
};

const Cell = ({ value, label, big = false }) => (
  <div className="text-center">
    <div className={`font-mono-num font-black tabular-nums leading-none ${big ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"} text-primary-yellow`}>
      {String(value).padStart(2, "0")}
    </div>
    <div className="text-[9px] uppercase tracking-[0.22em] text-white/40 mt-1">{label}</div>
  </div>
);

const matchLabel = (meta) => {
  if (!meta) return null;
  if (!meta.home_team || !meta.away_team) return null;
  return `${meta.home_team} vs ${meta.away_team}`;
};

const Banner = ({ remaining, meta, showCta }) => (
  <div
    data-testid="kickoff-banner"
    className="border-b border-primary-yellow/30 bg-gradient-to-r from-[#0F1116] via-[#15171C] to-[#0F1116]"
  >
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3 min-w-0">
        <SoccerBall size={20} weight="fill" className="text-primary-yellow shrink-0" />
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 font-semibold flex items-center gap-2">
            <Clock size={10} />
            {remaining.live ? "Season is live" : `Next kickoff${meta.matchweek ? ` · MW ${meta.matchweek}` : ""}`}
          </div>
          {matchLabel(meta) && !remaining.live && (
            <div className="font-heading text-sm sm:text-base uppercase font-black truncate text-white">
              {matchLabel(meta)}
            </div>
          )}
        </div>
      </div>
      {!remaining.live ? (
        <div className="flex items-center gap-3 sm:gap-5">
          <Cell value={remaining.days} label="d" />
          <span className="text-white/20 font-mono-num text-2xl leading-none">:</span>
          <Cell value={remaining.hours} label="h" />
          <span className="text-white/20 font-mono-num text-2xl leading-none">:</span>
          <Cell value={remaining.minutes} label="m" />
          <span className="text-white/20 font-mono-num text-2xl leading-none">:</span>
          <Cell value={remaining.seconds} label="s" />
        </div>
      ) : (
        <div className="text-primary-yellow font-bold uppercase tracking-widest text-xs animate-pulse">
          ● Live Now
        </div>
      )}
      {showCta && (
        <Link
          to="/pricing"
          data-testid="kickoff-banner-cta"
          className="hidden md:inline-flex items-center gap-2 bg-primary-yellow text-black px-4 py-2 text-[10px] uppercase tracking-[0.22em] font-bold hover:-translate-y-0.5 transition-transform"
        >
          <Crown size={12} weight="fill" /> Go Premier · £9.99
        </Link>
      )}
    </div>
  </div>
);

const Card = ({ remaining, meta, showCta }) => (
  <div
    data-testid="kickoff-card"
    className="surface-elevated border border-primary-yellow/30 glow-yellow p-6 sm:p-8 mb-10"
  >
    <div className="flex flex-wrap items-center justify-between gap-6">
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-[0.22em] text-primary-yellow font-bold flex items-center gap-2">
          <Clock size={12} weight="fill" />
          {remaining.live ? "Season is live" : "Season kicks off in"}
        </div>
        {matchLabel(meta) && !remaining.live && (
          <div className="font-heading text-xl sm:text-2xl uppercase font-black mt-1 truncate text-white">
            {matchLabel(meta)}
            {meta.matchweek ? <span className="text-white/40 text-sm font-normal ml-2 normal-case tracking-normal">· MW {meta.matchweek}</span> : null}
          </div>
        )}
      </div>
      {!remaining.live ? (
        <div className="flex items-center gap-4 sm:gap-6">
          <Cell value={remaining.days} label="days" big />
          <span className="text-white/15 font-mono-num text-3xl leading-none">:</span>
          <Cell value={remaining.hours} label="hrs" big />
          <span className="text-white/15 font-mono-num text-3xl leading-none">:</span>
          <Cell value={remaining.minutes} label="min" big />
          <span className="text-white/15 font-mono-num text-3xl leading-none">:</span>
          <Cell value={remaining.seconds} label="sec" big />
        </div>
      ) : (
        <div className="text-primary-yellow font-bold uppercase tracking-widest text-sm animate-pulse">
          ● Live Now
        </div>
      )}
    </div>
    {showCta && !remaining.live && (
      <p className="mt-5 text-sm text-white/60">
        Lock your <span className="text-primary-yellow font-bold">£9.99 Premier</span> pass before kickoff — see the full season&apos;s fixtures and run your own private leagues from day one.
      </p>
    )}
  </div>
);

export default KickoffCountdown;
